/**
 * TrustGate Action Service
 * 
 * Controlled write actions through Cloud Functions.
 * FAIL-CLOSED: All actions require authentication and go through functions.
 * NO direct Firestore writes from client.
 */

import { auth } from '../../firebase/init';
import type { TrustGateWhoami } from '../types';

const FUNCTIONS_BASE_URL = import.meta.env.VITE_TRUSTGATE_FUNCTIONS_URL || 
  'https://us-central1-trustgate-27ddc.cloudfunctions.net';

/**
 * Get Firebase ID token for authenticated requests
 */
async function getAuthToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch (error) {
    console.error('[TrustGateActionService] Failed to get auth token:', error);
    return null;
  }
}

/**
 * Make authenticated request to Cloud Function
 */
async function callFunction<T>(
  endpoint: string,
  method: 'GET' | 'POST',
  body?: unknown
): Promise<{ ok: boolean; data?: T; error?: string; code?: string }> {
  try {
    const token = await getAuthToken();
    if (!token) {
      return { ok: false, error: 'Not authenticated', code: 'UNAUTHORIZED' };
    }

    const url = `${FUNCTIONS_BASE_URL}${endpoint}`;
    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    };
    
    if (body) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);
    const data = await response.json();

    if (!response.ok) {
      return {
        ok: false,
        error: data.message || data.error || 'Request failed',
        code: data.code || `HTTP_${response.status}`,
      };
    }

    return { ok: true, data };
  } catch (error) {
    console.error(`[TrustGateActionService] ${endpoint} failed:`, error);
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Network error',
      code: 'NETWORK_ERROR',
    };
  }
}

// ============================================================
// ACTION REQUEST TYPES
// ============================================================

export interface CreateActionRequestParams {
  amount: number;          // Amount in cents (e.g., 50000 = $500.00)
  currency: string;        // e.g., 'USD'
  destination: string;     // Destination ID
  memo?: string;           // Optional memo (or human-readable summary for structured intents)
  // STRUCTURED INTENTS V2: Optional structured parameters
  intentType?: string;     // e.g., 'epoch_promote', 'module_install', 'epoch_rollback'
  params?: unknown;        // Structured parameters for intent execution
}

export interface CreateActionRequestResponse {
  ok: boolean;
  requestId: string;
  status: 'PENDING' | 'DENIED';
  decision: 'ALLOW' | 'DENY' | null;
  reasonCode?: string;
  paramsHash?: string;
  expiresAt?: number;
  correlationId?: string;
  // 402 Payment Required fields
  moduleId?: string;
  action?: string;
  checkoutUrl?: string;
}

export interface ApproveActionRequestParams {
  requestId: string;
}

export interface ApproveActionRequestResponse {
  ok: boolean;
  requestId: string;
  status: 'APPROVED';
  proofId: string;
  permitToken?: string;
  permitSig?: string;
  correlationId?: string;
}

export interface DenyActionRequestParams {
  requestId: string;
  reason?: string;
}

export interface DenyActionRequestResponse {
  ok: boolean;
  requestId: string;
  status: 'DENIED';
  reasonCode: string;
  correlationId?: string;
}

// ============================================================
// ACTION FUNCTIONS
// ============================================================

/**
 * Create a new action request (PAYMENT_RELEASE)
 * 
 * @param params - Request parameters (amount, currency, destination, memo)
 * @returns Response with requestId and status
 */
export async function createActionRequest(
  params: CreateActionRequestParams
): Promise<CreateActionRequestResponse> {
  const response = await callFunction<CreateActionRequestResponse>(
    '/createActionRequest',
    'POST',
    {
      amountCents: params.amount,
      currency: params.currency.toLowerCase(),
      destination: params.destination,
      memo: params.memo || '',
    }
  );

  if (!response.ok) {
    return {
      ok: false,
      requestId: '',
      status: 'DENIED',
      decision: 'DENY',
      reasonCode: response.code || 'UNKNOWN_ERROR',
    };
  }

  return response.data!;
}

/**
 * Approve an action request (generates proof)
 * 
 * @param params - Request ID to approve
 * @returns Response with proofId and permitToken
 */
export async function approveActionRequest(
  params: ApproveActionRequestParams
): Promise<ApproveActionRequestResponse> {
  const response = await callFunction<ApproveActionRequestResponse>(
    '/approveActionRequest',
    'POST',
    {
      requestId: params.requestId,
    }
  );

  if (!response.ok) {
    throw new Error(response.error || 'Approval failed');
  }

  return response.data!;
}

/**
 * Deny an action request (admin only)
 * 
 * @param params - Request ID and optional reason
 * @returns Response with status
 */
export async function denyActionRequest(
  params: DenyActionRequestParams
): Promise<DenyActionRequestResponse> {
  const response = await callFunction<DenyActionRequestResponse>(
    '/denyActionRequest',
    'POST',
    {
      requestId: params.requestId,
      reason: params.reason,
    }
  );

  if (!response.ok) {
    throw new Error(response.error || 'Denial failed');
  }

  return response.data!;
}

/**
 * Check if user has admin privileges (claim-based)
 * GOVERNANCE INVARIANT: Authority derives from custom claims ONLY
 * 
 * @returns True if user has trustgate_admin custom claim
 */
export async function checkApproverStatus(): Promise<boolean> {
  try {
    const response = await callFunction<TrustGateWhoami>(
      '/whoami',
      'GET'
    );

    if (!response.ok || !response.data) return false;
    
    // GOVERNANCE INVARIANT: Trust custom claim from whoami (not Firestore)
    return response.data.isAdmin === true;
  } catch (error) {
    console.error('[TrustGateActionService] Failed to check approver status:', error);
    return false;
  }
}

/**
 * Format amount for display
 * 
 * @param amountCents - Amount in cents
 * @param currency - Currency code
 * @returns Formatted amount string
 */
export function formatActionAmount(amountCents: number, currency: string): string {
  const amount = amountCents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount);
}

/**
 * Validate action request parameters
 * 
 * @param params - Request parameters
 * @returns Validation result
 */
export function validateActionRequest(
  params: CreateActionRequestParams
): { valid: boolean; error?: string } {
  if (!params.amount || params.amount <= 0) {
    return { valid: false, error: 'Amount must be greater than 0' };
  }

  if (!params.currency || params.currency.length !== 3) {
    return { valid: false, error: 'Invalid currency code' };
  }

  if (!params.destination || params.destination.trim().length === 0) {
    return { valid: false, error: 'Destination is required' };
  }

  if (params.memo && params.memo.length > 500) {
    return { valid: false, error: 'Memo must be 500 characters or less' };
  }

  return { valid: true };
}
